import { cloneElement, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Ico } from '@/ui/Ico'
import { cls } from '@/ui/cls'
import type { ContactInfoFilter } from '@/features/leads/workflow/queue'
import type { FocusSort } from './focusSort'
import HandoverCard from '@/features/leads/detail/HandoverCard'
import TimelineTab from '@/features/leads/detail/TimelineTab'
import DetailsTab from '@/features/leads/detail/DetailsTab'
import CompanyIntel from '@/features/leads/detail/CompanyIntel'
import QueueNavigator from './QueueNavigator'
import ReferralSheet from './ReferralSheet'
import OutcomeBar from '@/features/leads/workflow/OutcomeBar'
import { DoNotContactNotice, DoNotContactSheet, useCanMarkDoNotContact } from '@/features/leads/workflow/DoNotContact'
import { leadActions } from '@/features/leads/workflow/doNotContactRules'
import { trackEvent } from '@/shared/api/telemetry'
import { useLeadDetail } from '@/features/leads/detail/useLeadDetail'
import type { LeadRow } from '@/shared/types'
import type { OutcomeKind } from '@/shared/domain'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'

export type FocusTab = 'handover' | 'timeline' | 'details' | 'company'

// Dealing off a deck, in three beats: the card is picked up off the pile, swiped
// out to the side, then placed underneath it. The end of the exit lands on the
// same offset and scale as the back of the stack (DEPTH below), so the card you
// just finished visibly becomes the bottom card before it stops being drawn —
// that continuity is what sells it as one pile rather than a card leaving and
// some rectangles twitching.
//
// Timings are one gesture split across four keyframes rather than four
// animations: 0 rest, .30 lifted and out, .58 travelling back in, 1 filed at
// the back. The incoming card settles under it in parallel, so the next lead is
// readable well before the old one finishes being put away.
const LIFT = '0 26px 50px -14px rgba(10,10,10,0.34)'
const REST = '0 2px 10px -4px rgba(10,10,10,0.10)'

// The pile behind the active card, keyed by lead where it is rendered so a card
// travels between slots instead of being rebuilt in place.
//
// scaleX, not scale: the card is ~600px tall, so a uniform 0.98 also shrinks it
// 12px vertically and cancels the offset that makes the pile visible. Insetting
// only the width leaves `y` as exactly how much of each card shows below the one
// in front. The rotations are deliberately uneven — a stack of paper is never
// squared, and half a degree is the difference between a pile and a set of
// nested rectangles.
//
// Opaque, deliberately. These were 0.55 and 0.3, which made the whole stacking
// order decorative: a card sliding behind a translucent card shows straight
// through it, so nothing was ever really occluded and the placed card appeared
// as a grey smear over the pile. Real paper is opaque — depth comes from the
// offset, the width inset, and each sheet's own shadow.
const DEPTH = [
  { y: 11, scaleX: 0.986, rotate: -0.35, opacity: 1 },
  { y: 22, scaleX: 0.971, rotate: 0.5, opacity: 1 },
]
// Behind the whole pile. Where a finished card is put, and where the next one
// comes from when stepping back.
const BEHIND = { y: 34, scaleX: 0.955, rotate: -0.7, opacity: 0 }
const FRONT = { y: 0, scaleX: 1, rotate: 0 }

// A cycling deck, so the two directions are inverses rather than mirror images.
//
// Forward: the top card is picked up off the pile — lifted toward the viewer,
// shadow blooming — carried out just far enough to clear the pile's edge, then
// set down behind everything. It lands on BEHIND and only fades once it is
// there, so you see it become the back of the stack rather than disappear.
//
// Back: the reverse. The card comes from behind the pile, around its edge and
// onto the front, while the card it replaces drops one slot to DEPTH[0]. That
// drop is short and unlifted: it is not being carried anywhere, it is just no
// longer on top.
//
// Lateral travel is ~90px, not the 320px this had before. It does not clear the
// pile — a 920px card offset 90px still covers most of it — and it is not meant
// to: the offset is there to uncover the pile's left edge so you can see what
// the card is being placed behind. A long throw read as discarding it
// off-screen, which is the opposite of filing it.
const DECK = {
  enter: (dir: 'forward' | 'back' | null) =>
    dir === 'back'
      // zIndex 0, not 2. Stepping back, this card starts behind the pile, so it
      // has to be drawn behind it — entering at 2 put a card that is visibly at
      // the back of the stack in front of every card it was behind. The spring
      // to `settled` carries it through 1 on the way to 2, so it comes around
      // the pile's edge and onto the front, which is the exact inverse of the
      // forward exit.
      ? { ...BEHIND, x: -70, scale: 0.97, opacity: 1, boxShadow: LIFT, zIndex: 0 }
      : { ...DEPTH[0], x: 0, scale: 1, opacity: 1, boxShadow: REST, zIndex: 2 },
  settled: {
    ...FRONT, x: 0, scale: 1, opacity: 1, boxShadow: REST, zIndex: 2,
    transition: { type: 'spring' as const, stiffness: 380, damping: 30, mass: 0.85 },
  },
  filed: (dir: 'forward' | 'back' | null) => {
    if (dir === 'back') {
      return {
        ...DEPTH[0], x: 0, scale: 1, opacity: 0, boxShadow: REST, zIndex: 1,
        transition: { duration: 0.3, ease: [0.4, 0, 0.3, 1] as [number, number, number, number] },
      }
    }
    return {
      // Picked up, carried around the side, set down at the back. The
      // anticipation dip is 10px over 7% of the gesture — too small to read as
      // movement, enough that the lift looks driven rather than triggered.
      x: [0, -10, 90, 64, 0, 0],
      y: [0, 1, -26, -6, BEHIND.y, BEHIND.y],
      scale: [1, 0.999, 1.05, 1.01, 0.955, 0.955],
      rotate: [0, -0.6, 3.4, 1.6, BEHIND.rotate, BEHIND.rotate],
      opacity: [1, 1, 1, 1, 1, 0],
      boxShadow: [REST, REST, LIFT, LIFT, REST, REST],
      // In front while it is picked up and carried, behind as it is laid down.
      //
      // z-index is an integer property, so a 1 -> 0 ramp put the card behind the
      // pile the instant it left 1 — it was never visibly on top at all. The
      // large values are what buy the visible margin: above the pile (1) and the
      // incoming card (2) for the whole lift and swipe.
      //
      // The step at stop 3 rather than a straight run to 0 is what lands the
      // crossing inside the descent — it passes behind ~60ms before it settles,
      // so it tucks under the stack as it is placed instead of disappearing
      // while still up in the air.
      zIndex: [5, 5, 5, 2, 0, 0],
      transition: {
        duration: 0.56,
        // Two extra stops on the tail so the card sits at the back for a beat
        // before it stops being drawn. Fading it the instant it lands is what
        // made this read as vanishing rather than being put away.
        times: [0, 0.07, 0.34, 0.6, 0.82, 1],
        ease: [
          [0.4, 0, 1, 1],
          [0.18, 0, 0.36, 1],
          [0.4, 0, 0.5, 1],
          [0.3, 0, 0.25, 1],
          [0.4, 0, 1, 1],
        ] as [number, number, number, number][],
      },
    }
  },
}

interface Props {
  lead: LeadRow
  // The neighbouring lead rendered behind the active card during a nav
  // animation (the one emerging from the stack). NULL when no animation is
  // running or there's no neighbour to show.
  // 'forward' = Next; 'back' = Prev. Pure navigation, no persistence —
  // the BDR can step back and forth through the same queue freely.
  nav: 'forward' | 'back' | null
  listLength: number
  idx: number
  tab: FocusTab
  setTab: (t: FocusTab) => void
  // Full focus queue — required so the queue navigator popover can render
  // the searchable list of all available leads.
  list: LeadRow[]
  onPrev: () => void
  onSkip: () => void
  // Jump straight back to the first lead in the queue. Visible only when
  // there's somewhere to go back to (idx > 0). Bound to the Home key in
  // App so power-users can reset without aiming at the button.
  onFirst: () => void
  // Jump to an arbitrary lead in the queue. Powers the queue navigator's
  // number-jump and click-row interactions.
  onJumpTo: (leadId: string) => void
  onAction: (kind: OutcomeKind, comment?: string) => void
  onPostpone: (leadId: string, days: number) => void
  /** After the lead is marked Kontakta ej from the bar: moves on, like a closing outcome. */
  onMarkedDoNotContact: () => void
  // Contact-info filter — flips the focus queue between all leads, only
  // reachable, or only unreachable. The counts come from the parent so
  // the segmented chip badges read true (count == "what you'd see").
  contactInfo: ContactInfoFilter
  onContactInfoChange: (next: ContactInfoFilter) => void
  contactCounts: { all: number; reachable: number; unreachable: number }
  sort: FocusSort
  onSortChange: (next: FocusSort) => void
  bandCounts: Record<string | number, number> | null
  showAll: boolean
  onShowAllChange: (next: boolean) => void
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cls(
        'bg-transparent border-0 py-3 px-1 mx-3 first:ml-0 text-xs font-medium cursor-pointer',
        'inline-flex items-center gap-1.5 tracking-[0.01em] -mb-px',
        'border-b border-transparent',
        active ? 'text-ink-1000 border-b-ink-1000' : 'text-ink-500 hover:text-ink-1000',
      )}
    >
      {children}
    </button>
  )
}

export default function FocusView({
  lead: leadSummary, nav, list, listLength, idx, tab, setTab, onPrev, onSkip,
  onFirst, onJumpTo, onAction, onPostpone, onMarkedDoNotContact,
  contactInfo, onContactInfoChange, contactCounts, sort, onSortChange, bandCounts, showAll, onShowAllChange,
}: Props) {
  // The queue holds list rows; overlay the full card (timeline, notes,
  // AI brief) for the active lead.
  const lead = useLeadDetail(leadSummary)
  const prevIcon = cloneElement(Ico.arrowL, { width: 13, height: 13 })
  const nextIcon = cloneElement(Ico.arrowR, { width: 13, height: 13 })
  const firstIcon = cloneElement(Ico.first, { width: 13, height: 13 })

  // Queue navigator open state — anchored to the "n/m" counter in the
  // progress bar. Closes itself on Escape or click-outside.
  const [navOpen, setNavOpen] = useState(false)

  // The scroll container sits outside the per-lead card (so the card can swap
  // under it without remounting the scrollbar), which means advancing the
  // queue used to leave the next card wherever the previous one had been
  // scrolled to. Reset on every lead change so each card always opens at
  // its own top.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [lead.id])

  // Referral capture. The sheet writes the referred person onto the lead and
  // promotes them, then hands the note to the normal outcome flow, which logs
  // the call and asks when to ring the referral.
  const [referring, setReferring] = useState(false)
  const [markingDnc, setMarkingDnc] = useState(false)
  const canMarkDnc = useCanMarkDoNotContact()
  const actions = leadActions(lead)

  // Progress bar scrub state. The bar acts like a timeline: hover shows
  // a thin indicator + a small tooltip with the lead at that slot, click
  // jumps. Each lead occupies an equal-width slot — `positionFromEvent`
  // snaps the cursor X to the nearest slot center.
  const barRef = useRef<HTMLDivElement | null>(null)
  const [hoverPos, setHoverPos] = useState<number | null>(null)

  const positionFromEvent = (e: ReactMouseEvent<HTMLDivElement>): number | null => {
    const el = barRef.current
    if (!el || listLength <= 0) return null
    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (x < 0 || x > rect.width) return null
    const ratio = rect.width > 0 ? x / rect.width : 0
    const pos = Math.round(ratio * (listLength - 1)) + 1
    return Math.max(1, Math.min(listLength, pos))
  }
  const onBarMove = (e: ReactMouseEvent<HTMLDivElement>) => setHoverPos(positionFromEvent(e))
  const onBarLeave = () => setHoverPos(null)
  const onBarClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const p = positionFromEvent(e)
    if (p && list[p - 1]) onJumpTo(list[p - 1].id)
  }

  const onTab = (t: FocusTab) => {
    trackEvent('lead_detail_tab', { tab: t }, lead.id)
    setTab(t)
  }

  const hoverLead = hoverPos != null ? list[hoverPos - 1] : null
  const hoverCenterPct = hoverPos != null && listLength > 1
    ? ((hoverPos - 1) / (listLength - 1)) * 100
    : 0

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full min-h-0">
      {/* Progress bar */}
      <div className="flex items-center gap-3 px-6 py-2 bg-background border-b border-ink-200">
        <button
          className={cls(
            'w-7 h-7 bg-white border border-ink-200 rounded cursor-pointer grid place-items-center text-ink-700 hover:text-ink-1000 hover:border-ink-1000 p-0',
            idx === 0 && 'opacity-0 pointer-events-none',
          )}
          onClick={onFirst}
          title="Till första leaden (Home)"
          aria-label="Till första leaden"
          disabled={idx === 0}
        >
          {firstIcon}
        </button>
        <button
          className="w-7 h-7 bg-white border border-ink-200 rounded cursor-pointer grid place-items-center text-ink-700 hover:text-ink-1000 hover:border-ink-1000 p-0"
          onClick={onPrev}
          title="Föregående (←/↑)"
          aria-label="Föregående lead"
        >
          {prevIcon}
        </button>
        <div
          ref={barRef}
          onMouseMove={onBarMove}
          onMouseLeave={onBarLeave}
          onClick={onBarClick}
          className="flex-1 relative py-3 -my-3 cursor-pointer group"
          title="Klicka för att hoppa till en lead"
          role="slider"
          aria-valuemin={1}
          aria-valuemax={listLength}
          aria-valuenow={idx + 1}
          aria-label="Position i kön"
        >
          {/* Track + fill — slightly taller on hover so the affordance is obvious */}
          <div className="h-1 bg-ink-100 rounded-sm overflow-hidden transition-[height] group-hover:h-1.5">
            {/* A spring rather than a width transition: the queue advances one
                discrete slot at a time, and easing a step looks mechanical
                where a slight settle reads as the bar catching up. */}
            <m.div
              className="h-full bg-brand"
              animate={{ width: `${((idx + 1) / listLength) * 100}%` }}
              transition={{ type: 'spring', stiffness: 320, damping: 30, mass: 0.6 }}
            />
          </div>
          {/* Hover caret — glides between slots instead of teleporting, which is
              what makes the bar read as one scrubbable track rather than a row
              of separate targets. `y` is set here rather than by a -translate
              class because motion writes transform and would overwrite it. */}
          <AnimatePresence>
            {hoverPos != null && (
              <m.div
                className="absolute top-1/2 w-0.5 h-3 bg-ink-1000 pointer-events-none"
                initial={{ opacity: 0, scaleY: 0.4, y: '-50%' }}
                animate={{
                  opacity: 1,
                  scaleY: 1,
                  y: '-50%',
                  left: `calc(${hoverCenterPct}% - 1px)`,
                }}
                exit={{ opacity: 0, scaleY: 0.4, y: '-50%' }}
                transition={{
                  left: { type: 'spring', stiffness: 500, damping: 34, mass: 0.4 },
                  default: { duration: 0.12, ease: 'easeOut' },
                }}
              />
            )}
          </AnimatePresence>
          {/* Hover tooltip — anchored below the bar so the topbar above
              doesn't clip it. Horizontal anchor flips at the right edge so
              it doesn't escape the viewport. */}
          <AnimatePresence>
            {hoverLead && hoverPos != null && (
              <m.div
                className={cls(
                  'absolute top-full mt-1.5 pointer-events-none whitespace-nowrap z-10',
                  'bg-white border border-ink-200 px-2 py-1 text-[11px] text-ink-1000',
                  'shadow-[0_8px_20px_-8px_rgba(10,10,10,0.25)]',
                )}
                style={{ maxWidth: 240 }}
                initial={{ opacity: 0, y: -4 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  left: `${hoverCenterPct}%`,
                  // Flips at the right edge so the label cannot leave the
                  // viewport. Kept in the animation rather than a style
                  // transform, which motion would otherwise overwrite.
                  x: hoverCenterPct > 80 ? '-100%' : '-50%',
                }}
                exit={{ opacity: 0, y: -4 }}
                transition={{
                  left: { type: 'spring', stiffness: 500, damping: 34, mass: 0.4 },
                  default: { duration: 0.12, ease: 'easeOut' },
                }}
              >
                <span className="font-mono text-ink-500 tabular-nums">#{hoverPos}</span>
                <span className="text-ink-300 mx-1.5">·</span>
                <span className="font-semibold">{hoverLead.company || '(unnamed)'}</span>
              </m.div>
            )}
          </AnimatePresence>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setNavOpen(o => !o)}
            title="Hoppa till en specifik lead, ändra sorteringsordning eller filtrera på kontaktinfo"
            aria-haspopup="dialog"
            aria-expanded={navOpen}
            className={cls(
              'font-mono text-xs whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded cursor-pointer border bg-white transition-colors',
              navOpen
                ? 'border-ink-1000 text-ink-1000'
                : contactInfo !== 'all'
                  ? 'border-brand text-ink-1000 hover:border-brand-deep'
                  : 'border-ink-200 text-ink-700 hover:text-ink-1000 hover:border-ink-1000',
            )}
          >
            <span>
              <strong className="text-ink-1000 font-semibold">{idx + 1}</strong>
              <span className="text-ink-500"> / {listLength}</span>
            </span>
            {/* Active filter modifier — only renders when the BDR has flipped
                away from 'all', so the default counter reads clean. Makes
                the filtered state visible without opening the popover. */}
            {contactInfo !== 'all' && (
              <span className="text-[10px] font-sans uppercase tracking-widest text-ink-700 bg-brand px-1.5 py-px rounded-[2px]">
                {contactInfo === 'reachable' ? 'Kontaktbara' : 'Ingen kontakt'}
              </span>
            )}
            <svg
              viewBox="0 0 10 6"
              width="9"
              height="6"
              className={cls(
                'transition-transform text-ink-500',
                navOpen && 'rotate-180 text-ink-1000',
              )}
              aria-hidden="true"
            >
              <path d="M0 0l5 6 5-6z" fill="currentColor" />
            </svg>
          </button>
          {navOpen && (
            <QueueNavigator
              list={list}
              currentId={lead.id}
              onJump={onJumpTo}
              onClose={() => setNavOpen(false)}
              contactInfo={contactInfo}
              onContactInfoChange={onContactInfoChange}
              contactCounts={contactCounts}
              sort={sort}
              onSortChange={onSortChange}
              bandCounts={bandCounts}
              showAll={showAll}
              onShowAllChange={onShowAllChange}
            />
          )}
        </div>
        <button
          className="w-7 h-7 bg-white border border-ink-200 rounded cursor-pointer grid place-items-center text-ink-700 hover:text-ink-1000 hover:border-ink-1000 p-0"
          onClick={onSkip}
          title="Nästa (→/↓) — tryck ← för att gå tillbaka"
          aria-label="Nästa lead"
        >
          {nextIcon}
        </button>
      </div>

      {/* Card scroll area */}
      <div ref={scrollRef} className="overflow-y-auto px-6 py-3.5 pb-6 min-h-0">
        <div className="focus-stack relative max-w-[920px] mx-auto">
          {/* Each pile card is keyed by the lead it stands for, so when the queue
              moves the card at slot 2 travels to slot 1 rather than being
              rebuilt there. The one leaving the pile is the one being promoted,
              so it exits to the active card's resting transform and fades under
              it — the real card is already drawn on top at the same offset, so
              the handover is not visible. */}
          <AnimatePresence initial={false}>
            {list.slice(idx + 1, idx + 1 + DEPTH.length).map((behindLead, i) => (
              <m.div
                key={behindLead.id}
                aria-hidden="true"
                className="absolute inset-x-0 top-0 bottom-0 bg-white border border-ink-200 pointer-events-none"
                style={{ zIndex: 1 }}
                // Stepping back, the card joining the pile at the front slot
                // is the one just left, so it drops from the active card's
                // resting position. Otherwise a card joins at the back.
                initial={nav === 'back' && i === 0
                  ? { ...FRONT, opacity: 0 }
                  : BEHIND}
                animate={{
                  y: DEPTH[i].y,
                  scaleX: DEPTH[i].scaleX,
                  rotate: DEPTH[i].rotate,
                  opacity: DEPTH[i].opacity,
                  boxShadow: REST,
                }}
                exit={{ y: 0, scaleX: 1, rotate: 0, opacity: 0 }}
                transition={{
                  type: 'spring', stiffness: 340, damping: 28, mass: 0.9,
                  // The far card lags the near one. Same reason the anticipation
                  // dip exists: simultaneous motion reads as one rigid object.
                  delay: i * 0.04,
                  opacity: { duration: 0.18 },
                }}
              />
            ))}
          </AnimatePresence>

          <AnimatePresence initial={false} mode="popLayout" custom={nav}>
          <m.div
            key={lead.id}
            className="focus-card focus-card-active relative bg-white border border-ink-200"
            custom={nav}
            variants={DECK}
            initial="enter"
            animate="settled"
            exit="filed"
            style={{ transformOrigin: 'center bottom' }}
          >
            <nav className="flex border-b border-ink-200 px-6 gap-1">
              <TabButton active={tab === 'handover'} onClick={() => onTab('handover')}>
                Överlämning
              </TabButton>
              <TabButton active={tab === 'timeline'} onClick={() => onTab('timeline')}>
                Aktivitet <span className="font-mono text-[10px] text-ink-500">
                  {lead.timeline.length + lead.notes.length}
                </span>
              </TabButton>
              <TabButton active={tab === 'details'} onClick={() => onTab('details')}>
                Detaljer
              </TabButton>
              <TabButton active={tab === 'company'} onClick={() => onTab('company')}>
                Företag
              </TabButton>
            </nav>
            <div className="p-0 [&>.handover]:border-0">
              {tab === 'handover' && (
                <HandoverCard
                  lead={lead}
                  mode="canvas"
                />
              )}
              {tab === 'timeline' && <TimelineTab lead={lead} onAction={onAction} />}
              {tab === 'details' && <DetailsTab lead={lead} />}
              {tab === 'company' && <CompanyIntel lead={lead} />}
            </div>
          </m.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Action bar — a contained panel no wider than the lead card above it,
          rather than a full-bleed band, so it reads as part of the same
          stack of cards instead of a separate edge-to-edge footer. */}
      <footer className="px-6 pb-3 pt-2">
        <div className="max-w-[920px] mx-auto bg-white border border-ink-200 shadow-sm px-3 py-2.5">
          {actions.kind === 'doNotContact' ? (
            <DoNotContactNotice lead={lead} mark={actions.mark} />
          ) : (
            <OutcomeBar
              onAction={onAction}
              onReferStart={() => setReferring(true)}
              onPostpone={days => onPostpone(lead.id, days)}
              onDoNotContact={canMarkDnc ? () => setMarkingDnc(true) : undefined}
            />
          )}
        </div>
      </footer>

      {referring && (
        <ReferralSheet
          key={lead.id}
          lead={lead}
          onCancel={() => setReferring(false)}
          onDone={note => { setReferring(false); onAction('referred', note) }}
        />
      )}
      {markingDnc && (
        <DoNotContactSheet
          key={lead.id}
          lead={lead}
          mode="mark"
          onCancel={() => setMarkingDnc(false)}
          onDone={() => { setMarkingDnc(false); onMarkedDoNotContact() }}
        />
      )}
    </div>
  )
}
